// Nomina de todas las plantas (PP-70): la que se ve con el filtro en "Todas".
// Cada vehiculo y cada conductor de la empresa una sola vez, con una ficha por
// planta habilitada ("Planta A ✓ · Planta B ✕ antigüedad") y el filtro "No
// elegibles". Una lectura para todas las plantas (GET /provider/roster,
// ADR-025); con una planta filtrada la seccion sigue siendo Fleet.
//
// Las escrituras son por planta en la API, pero la nomina es de la empresa
// madre (ADR-014): el alta crea la fila en todas sus plantas y la edicion, la
// baja y la reincorporacion valen en todas. Por eso van por una habilitacion
// sin preguntar (el ancla de la fila, o la primera planta de la cuenta para
// el alta). La planta se pide solo para los documentos: cada planta exige
// tipos distintos y la ficha muestra lo que falta para la elegida.
//
// Que NO muestra la lista: telefono ni documento del conductor (solo el
// formulario), igual que la vista de una planta.
import { useId, useMemo, useState } from "react";
import type { CountByDocumentStatus, ProviderRosterSite, RosterHolder } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { VEHICLE_TYPE_LABEL } from "../logic/fleet.js";
import { formatCalendarDate } from "../logic/documents.js";
import {
  anchorDriver,
  anchorVehicle,
  chipText,
  documentsSummary,
  driverRowAfterWrite,
  driverRowFromCreate,
  onlyIneligible,
  siteChips,
  vehicleRowAfterWrite,
  vehicleRowFromCreate,
  type AllSitesDriverRow,
  type AllSitesRow,
  type AllSitesVehicleRow,
} from "../logic/roster.js";
import type { SiteAccess } from "../sites/port.js";
import { SubjectDocuments } from "./documents/SubjectDocuments.js";
import { DriverForm, VehicleForm } from "./forms.js";
import { HolderCell, RosterBadge, useHolders } from "./parts.js";
import type { FleetPort, FleetSite } from "./port.js";
import { RosterPanel, type Column } from "./RosterPanel.js";
import { RosterTabs, type RosterTab } from "./RosterTabs.js";
import { useAllSitesRoster } from "./useAllSitesRoster.js";
import type { RosterHandle } from "./useRoster.js";

interface Props {
  session: PortalSession;
  port: FleetPort;
  /** Habilitaciones de la cuenta, ordenadas; nunca vacia (el alta va por la primera). */
  sites: readonly SiteAccess[];
}

type Row = AllSitesRow<AllSitesVehicleRow["entry"] | AllSitesDriverRow["entry"]>;

export function SiteChips({ row, sites }: { row: Row; sites: readonly ProviderRosterSite[] }): React.JSX.Element {
  return (
    <ul aria-label={`Plantas de ${row.code}`} className="m-0 flex list-none flex-wrap gap-1 p-0">
      {siteChips(row.entry, sites).map((chip) => (
        <li key={`${chip.siteId}/${chip.providerId}`}>
          <Badge
            variant={chip.eligible ? "ok" : "critico"}
            title={chip.eligible ? "Elegible" : `No elegible: ${chip.detail}`}
          >
            {chipText(chip)}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

function DocumentsCell({ count }: { count: CountByDocumentStatus }): React.JSX.Element {
  return <span className={count.expired > 0 ? "text-xs text-critico-texto" : "text-xs text-texto-suave"}>{documentsSummary(count)}</span>;
}

function holderOf(holder: RosterHolder): RosterHolder | undefined {
  // Una fila provisoria de un alta sin titular en la respuesta no inventa uno.
  return holder.brandName === "" ? undefined : holder;
}

/** Cuantos estan en la nomina (las bajas no cuentan), sobre la lista completa. */
function count(state: RosterHandle<Row>["state"]): string {
  return state.estado === "listo" ? ` (${String(state.items.filter((row) => row.inRoster).length)})` : "";
}

/** La misma handle con la lista filtrada; las escrituras siguen yendo a la completa. */
function filtered<T extends Row>(handle: RosterHandle<T>, on: boolean): RosterHandle<T> {
  if (!on || handle.state.estado !== "listo") return handle;
  return { ...handle, state: { ...handle.state, items: onlyIneligible(handle.state.items) as T[] } };
}

/**
 * Ficha de documentos desde "Todas": si la fila esta en mas de una planta,
 * primero se elige contra que planta mirar lo que falta. El documento se carga
 * una vez y vale en todas.
 */
function DocumentsBySite({
  session,
  port,
  row,
  sites,
  label,
  onChanged,
  onClose,
}: {
  session: PortalSession;
  port: FleetPort;
  row: Row;
  sites: readonly ProviderRosterSite[];
  label: string;
  onChanged: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const chips = siteChips(row.entry, sites);
  const [chosen, setChosen] = useState<string | null>(chips.length === 1 ? `${chips[0]!.siteId}/${chips[0]!.providerId}` : null);
  const enablement = row.entry.sites.find((e) => `${e.siteId}/${e.providerId}` === chosen);
  const type = "plate" in row.entry ? ("vehicle" as const) : ("driver" as const);

  if (enablement === undefined) {
    return (
      <div role="group" aria-label={`Documentos de ${row.code}: elegir planta`} className="flex flex-col gap-2">
        <p className="m-0 text-sm text-texto">
          {`${label}: elegir la planta para ver lo que exige. El documento se carga una vez y vale en todas.`}
        </p>
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <Button key={`${chip.siteId}/${chip.providerId}`} size="sm" variant="secundario" onClick={() => setChosen(`${chip.siteId}/${chip.providerId}`)}>
              {chip.siteName}
            </Button>
          ))}
          <Button size="sm" variant="fantasma" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    );
  }
  const siteName = chips.find((chip) => chip.siteId === enablement.siteId && chip.providerId === enablement.providerId)?.siteName;
  return (
    <SubjectDocuments
      key={`${enablement.siteId}/${enablement.providerId}/${enablement.id}`}
      session={session}
      port={port}
      site={{ siteId: enablement.siteId, providerId: enablement.providerId }}
      subject={{ type, providerId: enablement.providerId, id: enablement.id }}
      subjectLabel={siteName === undefined ? label : `${label} · ${siteName}`}
      eligibility={enablement.eligibility}
      onChanged={onChanged}
      onClose={onClose}
    />
  );
}

export function AllSitesFleet({ session, port, sites }: Props): React.JSX.Element {
  const [tab, setTab] = useState<RosterTab>("vehicles");
  const [ineligibleOnly, setIneligibleOnly] = useState(false);
  const baseId = useId();
  const roster = useAllSitesRoster(port, session);
  const first = sites[0]!;
  const altaSite: FleetSite = { siteId: first.siteId, providerId: first.providerId };
  const holders = useHolders(port, session, altaSite);
  const rosterSites = roster.sites;
  const today = roster.vehicles.state.estado === "listo" ? roster.vehicles.state.today : "";

  const vehicleColumns = useMemo<readonly Column<AllSitesVehicleRow>[]>(() => [
    { header: "Código", cell: (row) => <span className="tabular font-semibold">{row.code}</span> },
    {
      header: "Vehículo",
      cell: ({ entry: v }) => (
        <div className="flex flex-col">
          <span className="tabular">{v.plate}</span>
          <span className="text-xs text-texto-suave">
            {VEHICLE_TYPE_LABEL[v.type]} · {v.capacity} pasajeros{v.year === undefined ? "" : ` · ${String(v.year)}`}
          </span>
        </div>
      ),
    },
    { header: "Titular", cell: (row) => <HolderCell holder={holderOf(row.entry.holder)} /> },
    { header: "Nómina", cell: (row) => <RosterBadge inRoster={row.inRoster} /> },
    { header: "Plantas", cell: (row) => <SiteChips row={row} sites={rosterSites} /> },
    { header: "Documentos", cell: (row) => <DocumentsCell count={row.entry.documents} /> },
  ], [rosterSites]);

  const driverColumns = useMemo<readonly Column<AllSitesDriverRow>[]>(() => [
    { header: "Código", cell: (row) => <span className="tabular font-semibold">{row.code}</span> },
    { header: "Nombre", cell: (row) => row.entry.name },
    { header: "Titular", cell: (row) => <HolderCell holder={holderOf(row.entry.holder)} /> },
    { header: "Nómina", cell: (row) => <RosterBadge inRoster={row.inRoster} /> },
    { header: "Plantas", cell: (row) => <SiteChips row={row} sites={rosterSites} /> },
    { header: "Documentos", cell: (row) => <DocumentsCell count={row.entry.documents} /> },
  ], [rosterSites]);

  const label: Record<RosterTab, string> = {
    vehicles: `Vehículos${count(roster.vehicles.state)}`,
    drivers: `Conductores${count(roster.drivers.state)}`,
  };
  // Tras una escritura la fila se ve enseguida con lo que devolvio la planta
  // ancla, y la relectura silenciosa trae las fichas de las demas plantas.
  const afterWrite = <T,>(handle: Pick<RosterHandle<Row>, "refresh">, value: T): T => {
    handle.refresh();
    return value;
  };

  return (
    <section aria-labelledby={`${baseId}-title`} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="m-0 text-eyebrow font-semibold uppercase tracking-eyebrow text-acero-texto">Todas las plantas</p>
          <h1 id={`${baseId}-title`} className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">
            Nómina
          </h1>
          <p className="m-0 text-xs text-texto-suave">
            {today === ""
              ? "Vehículos y conductores de la empresa, con la elegibilidad en cada planta."
              : `Vehículos y conductores de la empresa, con la elegibilidad en cada planta a su fecha operativa (${formatCalendarDate(today)}).`}
          </p>
        </div>
        <Button
          variant={ineligibleOnly ? "primario" : "secundario"}
          size="sm"
          aria-pressed={ineligibleOnly}
          onClick={() => setIneligibleOnly((on) => !on)}
        >
          No elegibles
        </Button>
      </div>

      <RosterTabs baseId={baseId} tab={tab} onChange={setTab} label={label}>
        {tab === "vehicles" ? (
          <RosterPanel
            key="vehicles-all"
            noun="vehículo"
            plural="los vehículos"
            emptyCopy={
              ineligibleOnly
                ? "Todos los vehículos son elegibles en todas sus plantas."
                : "La empresa todavía no tiene vehículos en la nómina. Usar Alta de vehículo para cargar el primero: queda en todas sus plantas."
            }
            roster={filtered(roster.vehicles, ineligibleOnly)}
            columns={vehicleColumns}
            rowLabel={(row) => `Vehículo ${row.code} · ${row.entry.plate}`}
            remove={(row) => port.deactivateVehicle(session, row.anchor, row.anchor.id).then((v) => afterWrite(roster.vehicles, vehicleRowAfterWrite(row, v)))}
            reinstate={(row) => port.reinstateVehicle(session, row.anchor, row.anchor.id).then((v) => afterWrite(roster.vehicles, vehicleRowAfterWrite(row, v)))}
            renderDocuments={(row, onClose) => (
              <DocumentsBySite
                key={row.id}
                session={session}
                port={port}
                row={row}
                sites={rosterSites}
                label={`Vehículo ${row.code} · ${row.entry.plate}`}
                onChanged={roster.vehicles.refresh}
                onClose={onClose}
              />
            )}
            renderForm={({ current, onSaved, onCancel }) => (
              <VehicleForm
                {...(current === undefined ? {} : { current: anchorVehicle(current) })}
                holders={holders}
                create={(body) => port.createVehicle(session, altaSite, body)}
                update={(_id, body) => port.updateVehicle(session, current!.anchor, current!.anchor.id, body)}
                onSaved={(v) =>
                  onSaved(afterWrite(roster.vehicles, current === undefined ? vehicleRowFromCreate(v) : vehicleRowAfterWrite(current, v)))}
                onCancel={onCancel}
              />
            )}
          />
        ) : (
          <RosterPanel
            key="drivers-all"
            noun="conductor"
            plural="los conductores"
            emptyCopy={
              ineligibleOnly
                ? "Todos los conductores son elegibles en todas sus plantas."
                : "La empresa todavía no tiene conductores en la nómina. Usar Alta de conductor para cargar el primero: queda en todas sus plantas."
            }
            roster={filtered(roster.drivers, ineligibleOnly)}
            columns={driverColumns}
            rowLabel={(row) => `Conductor ${row.code} · ${row.entry.name}`}
            remove={(row) => port.deactivateDriver(session, row.anchor, row.anchor.id).then((d) => afterWrite(roster.drivers, driverRowAfterWrite(row, d)))}
            reinstate={(row) => port.reinstateDriver(session, row.anchor, row.anchor.id).then((d) => afterWrite(roster.drivers, driverRowAfterWrite(row, d)))}
            renderDocuments={(row, onClose) => (
              <DocumentsBySite
                key={row.id}
                session={session}
                port={port}
                row={row}
                sites={rosterSites}
                label={`Conductor ${row.code} · ${row.entry.name}`}
                onChanged={roster.drivers.refresh}
                onClose={onClose}
              />
            )}
            renderForm={({ current, onSaved, onCancel }) => (
              <DriverForm
                {...(current === undefined ? {} : { current: anchorDriver(current) })}
                holders={holders}
                create={(body) => port.createDriver(session, altaSite, body)}
                update={(_id, body) => port.updateDriver(session, current!.anchor, current!.anchor.id, body)}
                onSaved={(d) =>
                  onSaved(afterWrite(roster.drivers, current === undefined ? driverRowFromCreate(d) : driverRowAfterWrite(current, d)))}
                onCancel={onCancel}
              />
            )}
          />
        )}
      </RosterTabs>
    </section>
  );
}
